import { useQuery } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';

import { FormSection, RichTextEditor } from '@nucleus-commerce/admin-kit/forms';
import { usePermissions } from '@nucleus-commerce/admin-kit/permissions';
import {
  Form,
  FormControl,
  FormDescription,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from '@nucleus-commerce/ui/components/form';
import { Input } from '@nucleus-commerce/ui/components/input';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@nucleus-commerce/ui/components/select';

import { policiesQueryOptions } from '@/features/policies';

import { fromConsent, toConsentPatch } from '../api/mappers';
import { useChatSettingsForm, type ChatSettingsTabProps } from '../hooks/use-chat-settings-form';
import { consentSchema, type ConsentForm } from '../schemas/consent-schema';

import { FormAlert, SaveButton, SwitchField } from './form-controls';

const GROUPS = ['consent'] as const;
const NONE = '__none__';

/** Policies are content; without `content.read` the handle is typed instead of picked. */
function PolicyHandleControl({
  storeId,
  value,
  onChange,
  disabled,
}: {
  storeId: string;
  value: string;
  onChange: (value: string) => void;
  disabled: boolean;
}) {
  const { t } = useTranslation('chatSettings');
  const { can } = usePermissions();
  const canList = can('content.read');
  const policies = useQuery({ ...policiesQueryOptions(storeId), enabled: canList });
  const items = policies.data ?? [];
  if (!canList || policies.isError) {
    return (
      <FormControl>
        <Input
          className="font-mono sm:w-80"
          value={value}
          disabled={disabled}
          placeholder="privacy-policy"
          onChange={(event) => onChange(event.target.value)}
        />
      </FormControl>
    );
  }
  const known = value === '' || items.some((policy) => policy.handle === value);
  return (
    <Select
      value={value === '' ? NONE : value}
      onValueChange={(next) => onChange(next === NONE ? '' : next)}
      disabled={disabled}
    >
      <FormControl>
        <SelectTrigger className="w-full sm:w-80">
          <SelectValue />
        </SelectTrigger>
      </FormControl>
      <SelectContent>
        <SelectItem value={NONE}>{t('consent.noPolicy')}</SelectItem>
        {known ? null : <SelectItem value={value}>{value}</SelectItem>}
        {items.map((policy) => (
          <SelectItem key={policy.id} value={policy.handle}>
            {policy.title} ({policy.handle})
          </SelectItem>
        ))}
      </SelectContent>
    </Select>
  );
}

/**
 * The privacy notice shoppers accept before the first message, and when the storefront may
 * create the visitor (`identifyOnLoad`, Q4) with what that means for the store's cookie banner.
 */
export function ConsentTab({ storeId, settings, readOnly }: ChatSettingsTabProps) {
  const { t } = useTranslation('chatSettings');
  const { form, submit, saving } = useChatSettingsForm<ConsentForm>({
    storeId,
    schema: consentSchema,
    defaultValues: fromConsent(settings),
    toPatch: toConsentPatch,
    groups: GROUPS,
    readOnly,
  });

  return (
    <Form {...form}>
      <form onSubmit={submit} noValidate className="flex max-w-3xl flex-col gap-6">
        <FormAlert message={form.formState.errors.root?.server?.message} />
        <FormSection title={t('consent.title')} description={t('consent.description')}>
          <SwitchField
            control={form.control}
            name="consent.enabled"
            label={t('fields.consentEnabled')}
            description={t('fields.consentEnabledHint')}
            disabled={readOnly}
          />
          <FormField
            control={form.control}
            name="consent.text"
            render={({ field }) => (
              <FormItem>
                <FormLabel>{t('fields.consentText')}</FormLabel>
                <FormControl>
                  <RichTextEditor
                    value={field.value}
                    onChange={(html) => field.onChange(html ?? '')}
                    onBlur={field.onBlur}
                    disabled={readOnly}
                  />
                </FormControl>
                <FormDescription>{t('fields.consentTextHint')}</FormDescription>
                <FormMessage />
              </FormItem>
            )}
          />
          <FormField
            control={form.control}
            name="consent.privacyPolicyHandle"
            render={({ field }) => (
              <FormItem>
                <FormLabel>{t('fields.privacyPolicy')}</FormLabel>
                <PolicyHandleControl
                  storeId={storeId}
                  value={field.value}
                  onChange={field.onChange}
                  disabled={readOnly}
                />
                <FormDescription>{t('fields.privacyPolicyHint')}</FormDescription>
                <FormMessage />
              </FormItem>
            )}
          />
          <SwitchField
            control={form.control}
            name="consent.usStateNotice"
            label={t('fields.usStateNotice')}
            description={t('fields.usStateNoticeHint')}
            disabled={readOnly}
          />
        </FormSection>
        <FormSection title={t('identify.title')}>
          <SwitchField
            control={form.control}
            name="consent.identifyOnLoad"
            label={t('fields.identifyOnLoad')}
            disabled={readOnly}
          />
          <p className="text-muted-foreground text-sm" data-testid="identify-explainer">
            {t('identify.explainer')}
          </p>
        </FormSection>
        <SaveButton saving={saving} dirty={form.formState.isDirty} hidden={readOnly} />
      </form>
    </Form>
  );
}
